import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, query, orderBy, limit, startAfter } from 'firebase/firestore';

const VenueContext = createContext();

// Common state abbreviations
const stateAbbreviations = {
  'ca': 'california',
  'ny': 'new york',
  'fl': 'florida',
  'tx': 'texas',
  'il': 'illinois',
  // Add more as needed
};

export const filterVenuesByLocation = (venues, location) => {
  if (!location) return venues;

  // Extract city and state from location
  const locationParts = location.toLowerCase().split(',').map(part => part.trim());
  const searchCity = locationParts[0];
  const searchState = locationParts[1];

  // Clean up search terms
  const cleanSearchCity = searchCity.replace(/^(the\s+|city\s+of\s+)/i, '');
  const cleanSearchState = searchState ? searchState.replace(/^(state\s+of\s+)/i, '') : '';

  // Try exact city and state match first
  let filteredVenues = venues.filter(venue => {
    const venueCity = venue.city?.toLowerCase().trim();
    const venueState = venue.state?.toLowerCase().trim();
    
    // Handle state abbreviations
    const searchStateExpanded = stateAbbreviations[cleanSearchState] || cleanSearchState;
    const venueStateExpanded = stateAbbreviations[venueState] || venueState;
    
    const cityMatch = venueCity === cleanSearchCity;
    const stateMatch = !searchState || venueStateExpanded === searchStateExpanded;
    
    return cityMatch && stateMatch;
  });

  // If no exact matches, try partial city matches
  if (filteredVenues.length === 0) {
    filteredVenues = venues.filter(venue => {
      const venueCity = venue.city?.toLowerCase().trim();
      const venueState = venue.state?.toLowerCase().trim();
      
      // Handle state abbreviations
      const searchStateExpanded = stateAbbreviations[cleanSearchState] || cleanSearchState;
      const venueStateExpanded = stateAbbreviations[venueState] || venueState;
      
      // More lenient city matching
      const cityMatch = 
        venueCity?.includes(cleanSearchCity) || 
        cleanSearchCity?.includes(venueCity) ||
        // Split words and check if any major word matches
        cleanSearchCity.split(/\s+/).some(word => 
          word.length > 2 && venueCity?.includes(word)
        );
      
      const stateMatch = !searchState || 
        venueStateExpanded === searchStateExpanded ||
        venueStateExpanded?.includes(searchStateExpanded) ||
        searchStateExpanded?.includes(venueStateExpanded);
      
      return cityMatch && stateMatch;
    });
  }

  // If still no matches and state is provided, try state matches
  if (filteredVenues.length === 0 && searchState) {
    filteredVenues = venues.filter(venue => {
      const venueState = venue.state?.toLowerCase().trim();
      
      // Handle state abbreviations
      const searchStateExpanded = stateAbbreviations[cleanSearchState] || cleanSearchState;
      const venueStateExpanded = stateAbbreviations[venueState] || venueState;
      
      return venueStateExpanded === searchStateExpanded ||
             venueStateExpanded?.includes(searchStateExpanded) ||
             searchStateExpanded?.includes(venueStateExpanded);
    });
  }

  // If still no matches, try very lenient matching
  if (filteredVenues.length === 0) {
    filteredVenues = venues.filter(venue => {
      const venueCity = venue.city?.toLowerCase().trim();
      const venueState = venue.state?.toLowerCase().trim();
      
      // Split search terms into words
      const searchWords = cleanSearchCity.split(/\s+/).filter(word => word.length > 2);
      
      // Check if any word matches either city or state
      return searchWords.some(word => 
        venueCity?.includes(word) || 
        venueState?.includes(word)
      );
    });
  }

  return filteredVenues;
};

export const filterVenuesBySearch = (venues, searchQuery) => {
  if (!searchQuery) return venues;
  
  const search = searchQuery.toLowerCase().trim();
  
  // First try exact name matches
  let filteredVenues = venues.filter(venue => 
    venue.name?.toLowerCase().trim() === search
  );

  // If no exact matches, try partial name matches
  if (filteredVenues.length <= 1) {
    filteredVenues = venues.filter(venue => {
      const venueName = venue.name?.toLowerCase().trim();
      const venueCity = venue.city?.toLowerCase().trim();
      const venueState = venue.state?.toLowerCase().trim();
      
      // Check for matches in name, city, or state
      return (
        venueName?.includes(search) ||
        search?.includes(venueName) ||
        venueCity?.includes(search) ||
        venueState?.includes(search)
      );
    });
  }

  // If still very few results, try more lenient matching
  if (filteredVenues.length <= 1) {
    filteredVenues = venues.filter(venue => {
      const venueName = venue.name?.toLowerCase().trim();
      const venueCity = venue.city?.toLowerCase().trim();
      const venueState = venue.state?.toLowerCase().trim();
      
      // Split search into words and check if any word matches
      const searchWords = search.split(' ');
      return searchWords.some(word => 
        venueName?.includes(word) ||
        venueCity?.includes(word) ||
        venueState?.includes(word)
      );
    });
  }

  return filteredVenues;
};

export function VenueProvider({ children }) {
  const [venues, setVenues] = useState([]);
  const [archivedVenues, setArchivedVenues] = useState([]);
  const [allVenues, setAllVenues] = useState([]); // Keep track of all venues for search/filter
  const [loading, setLoading] = useState(true);
  const [hasMoreVenues, setHasMoreVenues] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadAllVenuesForSearch = async () => {
    try {
      const db = getFirestore();
      const allVenuesSnapshot = await getDocs(collection(db, 'venues'));
      const allVenuesData = allVenuesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllVenues(allVenuesData);
      return allVenuesData;
    } catch (error) {
      console.error('Error loading all venues:', error);
      return [];
    }
  };

  const loadVenues = async (isLoadingMore = false) => {
    try {
      if (!isLoadingMore) {
        setLoading(true);
        // Load all venues for search/filtering but only display first 30
        const allVenuesData = await loadAllVenuesForSearch();
        console.log('Loaded all venues for search:', allVenuesData.length);
        setVenues(allVenuesData);
        // setHasMoreVenues(allVenuesData.length > 30);
      } else {
        setLoadingMore(true);
        // Load more from the allVenues array
        const currentLength = venues.length;
        const nextBatch = allVenues.slice(currentLength, currentLength + 30);
        setVenues(prevVenues => [...prevVenues, ...nextBatch]);
        setHasMoreVenues(currentLength + 30 < allVenues.length);
      }
    } catch (error) {
      console.error('Error loading venues:', error);
      if (!isLoadingMore) {
        setVenues([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadArchivedVenues = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const archivedVenuesSnapshot = await getDocs(collection(db, 'archived_venues'));
      const archivedVenuesData = archivedVenuesSnapshot.docs.map(doc => ({
        id: doc.id, 
        ...doc.data()
      }));
      setArchivedVenues(archivedVenuesData);
    } catch (error) {
      console.error('Error loading archived venues:', error);
      setArchivedVenues([]);  
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVenues();
    loadArchivedVenues();
  }, []);

  const loadMoreVenues = () => {
    if (hasMoreVenues && !loadingMore) {
      loadVenues(true);
    }
  };

  const refreshVenues = () => {
    setHasMoreVenues(true);
    loadVenues();
    loadArchivedVenues();
  };

  return (
    <VenueContext.Provider value={{ 
      venues, 
      allVenues, // Provide access to all venues for search/filter
      archivedVenues,
      loading, 
      loadingMore,
      hasMoreVenues,
      refreshVenues,
      loadMoreVenues,
      filterVenuesByLocation,
      filterVenuesBySearch
    }}>
      {children}
    </VenueContext.Provider>
  );
}

export const useVenues = () => {
  const context = useContext(VenueContext);
  if (!context) {
    throw new Error('useVenues must be used within a VenueProvider');
  }
  return context;
}; 